import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AccountSummary } from '@flugwettrennen/shared';
import { api, ApiError } from '../api';
import { useAuth } from '../auth';
import { dateTime } from '../lib/format';

interface Invite {
  code: string;
  usedBy: string | null;
  createdAt: string;
}

export function AdminPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [users, setUsers] = useState<AccountSummary[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function reload() {
    api.admin.listInvites().then((r) => setInvites(r.invites)).catch(() => undefined);
    api.admin.listUsers().then((r) => setUsers(r.users)).catch(() => undefined);
  }
  useEffect(reload, []);

  async function create() {
    await api.admin.createInvite();
    reload();
  }

  function copy(code: string) {
    void navigator.clipboard?.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  }

  async function removeUser(id: string) {
    setError(null);
    setBusy(true);
    try {
      await api.admin.deleteUser(id);
      setConfirming(null);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? t([`errors.${err.code}`, 'errors.generic']) : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container page stack">
      <div>
        <div className="eyebrow">{t('nav.admin')}</div>
        <h1>{t('admin.title')}</h1>
        <p className="muted" style={{ marginTop: -4 }}>{t('admin.subtitle')}</p>
      </div>
      <div>
        <button className="btn btn-primary" onClick={() => void create()} data-testid="create-invite">
          {t('admin.create')}
        </button>
      </div>
      <div className="card">
        <div className="table-wrap">
          <table className="mono" style={{ fontSize: '0.9rem' }}>
            <tbody>
              {invites.map((iv) => (
                <tr key={iv.code} style={{ borderBottom: '1px solid var(--line)' }}>
                  <td style={{ padding: '8px' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => copy(iv.code)}>
                      {copied === iv.code ? t('admin.copied') : iv.code}
                    </button>
                  </td>
                  <td style={{ padding: '8px' }}>
                    <span className={`pill ${iv.usedBy ? 'pill-neutral' : 'pill-go'}`}>
                      {iv.usedBy ? t('admin.used') : t('admin.unused')}
                    </span>
                  </td>
                  <td style={{ padding: '8px', color: 'var(--muted)' }}>{dateTime(iv.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <div className="eyebrow">{t('admin.usersTitle')}</div>
        <p className="muted" style={{ marginTop: 2 }}>{t('admin.usersSubtitle')}</p>
      </div>
      {error && <div className="notice notice-error">{error}</div>}
      <div className="card">
        <div className="table-wrap">
          <table style={{ fontSize: '0.9rem' }}>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === user?.id;
                return (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--line)' }}>
                    <td style={{ padding: '8px' }}>
                      <strong>{u.displayName}</strong>
                      <div className="mono muted" style={{ fontSize: '0.8rem' }}>{u.email}</div>
                    </td>
                    <td style={{ padding: '8px' }}>
                      {u.isAdmin && <span className="pill pill-neutral">{t('admin.roleAdmin')}</span>}
                    </td>
                    <td style={{ padding: '8px', color: 'var(--muted)' }}>
                      {t('admin.roundCount', { count: u.rounds })}
                    </td>
                    <td style={{ padding: '8px', color: 'var(--muted)' }}>{dateTime(u.createdAt)}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {isSelf ? (
                        <span className="muted" style={{ fontSize: '0.8rem' }}>{t('admin.self')}</span>
                      ) : confirming === u.id ? (
                        <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-danger btn-sm"
                            type="button"
                            disabled={busy}
                            onClick={() => void removeUser(u.id)}
                            data-testid={`user-delete-confirm-${u.id}`}
                          >
                            {t('admin.userDeleteConfirm')}
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            onClick={() => setConfirming(null)}
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn btn-sm"
                          type="button"
                          onClick={() => {
                            setError(null);
                            setConfirming(u.id);
                          }}
                          data-testid={`user-delete-${u.id}`}
                        >
                          {t('admin.userDelete')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="muted" style={{ fontSize: '0.85rem', marginTop: -4 }}>{t('admin.userDeleteHint')}</p>
    </div>
  );
}
